import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BookOpen,
  ArrowRight,
  Library as LibraryIcon,
  Target,
  Download,
  GraduationCap,
} from 'lucide-react';
import AssessmentModal from '../components/AssessmentModal';
import UsageLimitBanner from '../components/UsageLimitBanner';
import DashboardHeader from '../components/DashboardHeader';
import DashboardGamificationHub from '../components/DashboardGamificationHub';
import PlacementTestModal from '../components/PlacementTestModal';
import JustAddedShelf from '../components/JustAddedShelf';
import OnboardingChecklist from '../components/OnboardingChecklist';
import { checkAuth, authFetch } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const Dashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [userRole, setUserRole] = useState('user');
  const [isAuthenticated, setIsAuthenticated] = useState(
    location.state?.user ? true : null,
  );
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [statsError, setStatsError] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [hasOfflineLibraryAccess, setHasOfflineLibraryAccess] = useState(false);
  const [showAssessment, setShowAssessment] = useState(false);
  const [showPlacement, setShowPlacement] = useState(false);
  const [placementTaken, setPlacementTaken] = useState(null); // null | true | false
  const [placementCanRetake, setPlacementCanRetake] = useState(true);
  const [placementNextAt, setPlacementNextAt] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/stats`);
      if (!response.ok) {
        throw new Error(`Stats request failed with status ${response.status}`);
      }
      const data = await response.json();
      setStats(data);
      setStatsError(false);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Aug 19 2026 — surface an explicit error flag instead of silently
      // leaving `stats` as `null`, so the dashboard header can show a
      // real "unavailable / try again" state rather than defaulting
      // gamified numbers to 0 as if that were confirmed data.
      setStatsError(true);
    }
  }, []);

  const fetchSubscription = useCallback(async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/api/subscription/status`);
      const data = await response.json();
      setSubscription(data);
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
    }
  }, []);

  const checkOfflineAccess = useCallback(async () => {
    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/offline-library/check-access`,
      );
      const data = await response.json();
      setHasOfflineLibraryAccess(!!data.has_access);
    } catch {
      setHasOfflineLibraryAccess(false);
    }
  }, []);

  const checkPlacement = useCallback(async () => {
    try {
      const r = await authFetch(`${BACKEND_URL}/api/placement/status`);
      if (!r.ok) return;
      const d = await r.json();
      setPlacementTaken(!!d.taken);
      setPlacementCanRetake(d.can_retake !== false);
      setPlacementNextAt(d.next_attempt_at || null);
      // First-time auto-prompt for newer users who haven't taken + haven't dismissed
      if (!d.taken && !localStorage.getItem('placement_prompt_dismissed')) {
        // Slight delay so it doesn't pop before the dashboard has painted
        setTimeout(() => setShowPlacement(true), 600);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('userRole') || 'user';
    setUserRole(role);

    const boot = async () => {
      try {
        const userData = location.state?.user || (await checkAuth());
        if (!userData) {
          setIsAuthenticated(false);
          navigate('/login');
          return;
        }
        setUser(userData);
        setIsAuthenticated(true);
        await Promise.all([
          fetchStats(),
          fetchSubscription(),
          checkOfflineAccess(),
          checkPlacement(),
        ]);
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    boot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, navigate]);

  // One-time greeting for users who just applied as a scribe via Google.
  useEffect(() => {
    if (location.state?.justAppliedAsWriter) {
      toast.success(
        '✍️ Scribe application submitted! An admin will review your account shortly.',
        { duration: 7000 },
      );
      // Clear the flag so a refresh doesn't re-show the toast.
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleAssessmentComplete = (results) => {
    setShowAssessment(false);
    localStorage.setItem('assessment_completed', 'true');
    toast.success(
      `Assessment complete!\nSpeed: ${results.wpm} WPM\nComprehension: ${results.comprehension}%`,
      { duration: 5000 },
    );
    fetchStats();
  };

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg mx-auto mb-4 animate-pulse">
            <BookOpen className="w-8 h-8 text-white" />
          </div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <main
      className="sharp-bg min-h-screen max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-8"
      data-testid="dashboard-home"
    >
      {/* Greeting + 3 stat cards */}
      <DashboardHeader
        user={user}
        stats={stats}
        statsError={statsError}
        subscription={subscription}
        onRetryStats={fetchStats}
      />

      {/* Verified rewards, challenges, achievements, and existing journey links. */}
      <DashboardGamificationHub />

      {/* Usage Limit Banner */}
      <div className="mb-6 sm:mb-8">
        <UsageLimitBanner />
      </div>

      {/* Just-added shelf — fresh content from the last 7 days (hides when empty) */}
      <JustAddedShelf />

        {/* First-week guided journey — auto-hides once complete or after 30d */}
        <OnboardingChecklist user={user} placementTaken={placementTaken} />

        {/* Placement test CTA — always present so users can retake */}
        <button
          onClick={() => {
            if (placementTaken && !placementCanRetake) {
              const niceDate = placementNextAt
                ? new Date(placementNextAt).toLocaleDateString(undefined, {
                    month: 'short', day: 'numeric', year: 'numeric',
                  })
                : 'next month';
              toast.error(`Placement test re-take unlocks on ${niceDate}.`);
              return;
            }
            setShowPlacement(true);
          }}
          className={`w-full mb-6 group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-rose-500/15 border ${placementTaken && !placementCanRetake ? 'border-amber-500/15 opacity-70 cursor-not-allowed' : 'border-amber-500/30 hover:border-amber-400/60'} transition-all text-left`}
          data-testid="home-cta-placement"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shrink-0 shadow-lg shadow-amber-500/20">
            <Target className="w-6 h-6 text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-amber-300 mb-0.5">
              {placementTaken
                ? (placementCanRetake ? 'Retake anytime' : 'One test per month')
                : 'Recommended for new readers'}
            </p>
            <p className="text-lg font-semibold text-white">
              {placementTaken
                ? (placementCanRetake ? 'Retake Placement Test' : 'Placement Test — locked')
                : 'Take the Placement Test'}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {placementTaken && !placementCanRetake && placementNextAt
                ? `Unlocks on ${new Date(placementNextAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
                : 'Find your level in ~3 minutes · +50 coins on completion'}
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-amber-300 opacity-70 group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Reading Courses CTA — drives users into lessons to earn XP/coins */}
        <button
          onClick={() => navigate('/reading-course')}
          className="w-full mb-6 group flex items-center gap-4 p-5 rounded-2xl bg-gradient-to-br from-sky-500/15 via-indigo-500/10 to-purple-500/15 border border-sky-500/30 hover:border-sky-400/60 transition-all text-left"
          data-testid="home-cta-reading-courses"
        >
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-400 to-indigo-500 flex items-center justify-center shrink-0 shadow-lg shadow-sky-500/20">
            <GraduationCap className="w-6 h-6 text-slate-900" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs uppercase tracking-widest text-sky-300 mb-0.5">
              Train your reading
            </p>
            <p className="text-lg font-semibold text-white">Reading Courses</p>
            <p className="text-xs text-gray-400 mt-0.5">
              4 tracks · 20 short lessons · earn XP, coins & levels
            </p>
          </div>
          <ArrowRight className="w-5 h-5 text-sky-300 opacity-70 group-hover:translate-x-1 transition-transform" />
        </button>

        {/* Quick actions to Progress / Library */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          <button
            onClick={() => navigate('/library')}
            className="group flex items-center justify-between p-5 rounded-2xl bg-gradient-to-br from-indigo-600/20 to-purple-700/20 border border-indigo-500/30 hover:border-indigo-400/60 transition-all text-left"
            data-testid="home-cta-library"
          >
            <div>
              <p className="text-xs uppercase tracking-widest text-indigo-300 mb-1">
                Jump in
              </p>
              <p className="text-lg font-semibold text-white">Browse Library</p>
              <p className="text-xs text-gray-400 mt-1">
                Ebooks · audiobooks · cinematic
              </p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-indigo-500/30 transition-colors">
              <LibraryIcon className="w-5 h-5 text-indigo-200" />
            </div>
          </button>

          <button
            onClick={() => navigate('/progress')}
            className="group flex items-center justify-between p-5 rounded-2xl bg-gradient-to-br from-emerald-600/20 to-teal-700/20 border border-emerald-500/30 hover:border-emerald-400/60 transition-all text-left"
            data-testid="home-cta-progress"
          >
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-300 mb-1">
                Track yourself
              </p>
              <p className="text-lg font-semibold text-white">View Progress</p>
              <p className="text-xs text-gray-400 mt-1">
                Skills · achievements · rewards
              </p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-emerald-500/30 transition-colors">
              <ArrowRight className="w-5 h-5 text-emerald-200" />
            </div>
          </button>
        </div>

        {/* Offline library CTA — only for users who've been granted access */}
        {hasOfflineLibraryAccess && (
          <button
            onClick={() => navigate('/offline-library')}
            className="w-full mb-8 group flex items-center justify-between p-5 rounded-2xl bg-gradient-to-br from-purple-600/20 to-fuchsia-700/20 border border-purple-500/30 hover:border-purple-400/60 transition-all text-left"
            data-testid="home-cta-offline-library"
          >
            <div>
              <p className="text-xs uppercase tracking-widest text-purple-300 mb-1">
                Premium perk
              </p>
              <p className="text-lg font-semibold text-white">Offline Library</p>
              <p className="text-xs text-gray-400 mt-1">
                Read / listen to your downloaded content without Wi-Fi
              </p>
            </div>
            <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-purple-500/30 transition-colors">
              <Download className="w-5 h-5 text-purple-200" />
            </div>
          </button>
        )}

        <AssessmentModal
          isOpen={showAssessment}
          onClose={() => setShowAssessment(false)}
          onComplete={handleAssessmentComplete}
        />

        {showPlacement && (
          <PlacementTestModal
            onClose={() => {
              setShowPlacement(false);
              localStorage.setItem('placement_prompt_dismissed', '1');
            }}
            onComplete={() => {
              setPlacementTaken(true);
              localStorage.setItem('placement_prompt_dismissed', '1');
              fetchStats();
            }}
          />
        )}
    </main>
  );
};

export default Dashboard;
